import { useState } from "react";

type Tier = 'starter' | 'builder' | 'master';

function fv(principal: number, rate: number, years: number, annual = 0): number {
  let v = principal;
  for (let y = 0; y < years; y++) v = v * (1 + rate / 100) + annual;
  return v;
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n)}`;
}

type Choice = { label: string; emoji: string; rate: number; note: string; annual?: number; years?: number };
type Round = { situation: string; character: string; startAmount: number; years: number; choices: Choice[]; bestIdx: number; lesson: string };

const ROUNDS: Record<Tier, Round[]> = {
  starter: [
    {
      situation: "You got $50 for your birthday! 🎂 Where do you put it?",
      character: "🧒 Birthday Kid",
      startAmount: 50, years: 3,
      choices: [
        { label: "Spend on toys now", emoji: "🧸", rate: 0, note: "Fun now — but it's gone!" },
        { label: "Keep under mattress", emoji: "🛏️", rate: 0, note: "Safe, but doesn't grow" },
        { label: "Open a savings account", emoji: "🏦", rate: 5, note: "5% interest every year!" },
      ],
      bestIdx: 2,
      lesson: "A savings account earns interest — your money makes MORE money just by sitting there!",
    },
    {
      situation: "You earned $80 doing chores all summer! 🧹 What's your move?",
      character: "🧒 Hard Worker",
      startAmount: 80, years: 5,
      choices: [
        { label: "Spend on video games", emoji: "🎮", rate: 0, note: "Zero savings left" },
        { label: "Keep in a jar at home", emoji: "🫙", rate: 0, note: "$80 forever — no growth" },
        { label: "Bank savings (5%/year)", emoji: "🏦", rate: 5, note: "Grows even while you sleep!" },
      ],
      bestIdx: 2,
      lesson: "Saving in a bank beats keeping cash at home. Your $80 keeps growing every single year!",
    },
    {
      situation: "You have $100 saved! The bank pays 6% per year. Leave it in or spend it?",
      character: "🧒 Smart Saver",
      startAmount: 100, years: 4,
      choices: [
        { label: "Buy snacks and games", emoji: "🍕", rate: 0, note: "Gone in a day!" },
        { label: "Take out the interest yearly", emoji: "💸", rate: 0, note: "No compounding — loses the magic" },
        { label: "Leave it in all 4 years", emoji: "🌱", rate: 6, note: "Interest earns more interest!" },
      ],
      bestIdx: 2,
      lesson: "Leaving your money in longer means the interest earns MORE interest. That's compounding!",
    },
  ],
  builder: [
    {
      situation: "You have $500 saved at 14. Where should you keep it for 10 years?",
      character: "💼 You (age 14)",
      startAmount: 500, years: 10,
      choices: [
        { label: "Checking account (0.1%)", emoji: "💳", rate: 0.1, note: "Almost zero growth" },
        { label: "High-yield savings (5%)", emoji: "🏦", rate: 5, note: "Safe and steady" },
        { label: "Index fund (avg 7%)", emoji: "📊", rate: 7, note: "Historical market return" },
      ],
      bestIdx: 2,
      lesson: "Over 10 years, the difference between 0.1% and 7% is massive. Index funds beat savings accounts for money you won't need soon.",
    },
    {
      situation: "Invest $200 at 16 for 20 years, OR wait and invest $400 at 26 for 10 years. At 7%/yr, who wins at 36?",
      character: "💼 You (age 16)",
      startAmount: 200, years: 20,
      choices: [
        { label: "Wait: $400 at age 26", emoji: "⌛", rate: 7, years: 10, note: "More money, only 10 years" },
        { label: "Start: $200 at 16 for 20yr", emoji: "⏰", rate: 7, note: "Less money — but 20 years!" },
        { label: "$200 now + add $50/year", emoji: "🚀", rate: 7, annual: 50, note: "Start early AND keep adding!" },
      ],
      bestIdx: 2,
      lesson: "Starting early beats starting with more money. Time is your biggest investing asset — don't wait for 'enough.'",
    },
    {
      situation: "You're 15 with $1,000 to invest for 20 years. Which return rate changes your future most?",
      character: "💼 You (age 15)",
      startAmount: 1000, years: 20,
      choices: [
        { label: "Savings account: 3%/yr", emoji: "🏦", rate: 3, note: "Low risk, low reward" },
        { label: "Bonds: 5%/year", emoji: "📜", rate: 5, note: "Medium stability" },
        { label: "Index fund: 10%/year", emoji: "📈", rate: 10, note: "Historical long-run average" },
      ],
      bestIdx: 2,
      lesson: "Over 20 years, 3% vs 10% turns $1K into $1.8K vs $6.7K. Small rate differences compound into massive outcomes.",
    },
  ],
  master: [
    {
      situation: "You're 18 with $2,000 in graduation gifts. 47 years until age 65. What do you do?",
      character: "🎓 You at 18",
      startAmount: 2000, years: 47,
      choices: [
        { label: "Spend on experiences now", emoji: "✈️", rate: 0, note: "Great memories, $0 at 65" },
        { label: "High-yield savings (5%)", emoji: "🏦", rate: 5, note: "Inflation erodes real returns" },
        { label: "Roth IRA index fund (10%)", emoji: "📈", rate: 10, note: "Tax-free retirement wealth" },
      ],
      bestIdx: 2,
      lesson: "$2K at 10% for 47 years ≈ $234K tax-free in a Roth IRA. Rule of 72: at 10%, money doubles every 7.2 years.",
    },
    {
      situation: "Inflation is 3%/yr. What's the real purchasing power of $10,000 over 30 years?",
      character: "🏛️ Inflation Fighter",
      startAmount: 10000, years: 30,
      choices: [
        { label: "Keep in cash (0%)", emoji: "💸", rate: 0, note: "Inflation cuts buying power ~60%" },
        { label: "Savings account (3%)", emoji: "🏦", rate: 3, note: "Just barely breaking even" },
        { label: "Index fund (7% nominal)", emoji: "📈", rate: 7, note: "4% real return after inflation" },
      ],
      bestIdx: 2,
      lesson: "Real return = Nominal − Inflation. Cash loses ~60% purchasing power over 30 years. Equities are the primary long-run inflation hedge.",
    },
    {
      situation: "Contribute $500/yr to a Roth IRA: start at 18 for 47yr vs start at 28 for 37yr. At 10%, who wins?",
      character: "🎓 Early Starter",
      startAmount: 0, years: 47,
      choices: [
        { label: "Start at 28 ($500/yr, 37yrs)", emoji: "⌛", rate: 10, annual: 500, years: 37, note: "37 years of contributions" },
        { label: "Start at 18 ($500/yr, 47yrs)", emoji: "⚡", rate: 10, annual: 500, note: "47 years of compounding" },
        { label: "Start at 18 ($1,000/yr, 47yrs)", emoji: "🚀", rate: 10, annual: 1000, note: "Max early Roth power" },
      ],
      bestIdx: 2,
      lesson: "Starting 10 years earlier with the same $500/yr produces 2–3× more wealth by retirement. Start date matters more than contribution size.",
    },
  ],
};

type ExploreConfig = {
  intro: string;
  amountDefault: number; amountMin: number; amountMax: number; amountStep: number;
  yearsDefault: number; yearsMin: number; yearsMax: number;
  rateDefault: number;
  ratePresets?: { label: string; rate: number; emoji: string }[];
  rateMin?: number; rateMax?: number;
  showFormula: boolean;
  inflationRate: number;
};

const EXPLORE_CFG: Record<Tier, ExploreConfig> = {
  starter: {
    intro: "Drag the sliders and watch your money grow! 🌱",
    amountDefault: 50, amountMin: 10, amountMax: 200, amountStep: 10,
    yearsDefault: 5, yearsMin: 1, yearsMax: 10,
    rateDefault: 5,
    ratePresets: [
      { label: "Mattress", rate: 0, emoji: "🛏️" },
      { label: "Piggy Bank", rate: 2, emoji: "🐷" },
      { label: "Bank", rate: 5, emoji: "🏦" },
    ],
    showFormula: false,
    inflationRate: 0,
  },
  builder: {
    intro: "Adjust the sliders to see compound interest in action.",
    amountDefault: 1000, amountMin: 100, amountMax: 5000, amountStep: 100,
    yearsDefault: 10, yearsMin: 1, yearsMax: 20,
    rateDefault: 7, rateMin: 1, rateMax: 15,
    showFormula: true,
    inflationRate: 0,
  },
  master: {
    intro: "Model long-term wealth with real market assumptions.",
    amountDefault: 5000, amountMin: 1000, amountMax: 50000, amountStep: 1000,
    yearsDefault: 20, yearsMin: 5, yearsMax: 40,
    rateDefault: 10, rateMin: 1, rateMax: 20,
    showFormula: true,
    inflationRate: 3,
  },
};

export default function CompoundGame({ onComplete, color, dark, tier = 'builder' }: {
  onComplete: (score?: number) => void;
  color: string; dark: string; tier?: string;
}) {
  const t: Tier = (tier === 'starter' || tier === 'master') ? tier : 'builder';
  const ec = EXPLORE_CFG[t];
  const rounds = ROUNDS[t];

  const [mode, setMode] = useState<'explore' | 'challenge' | null>(null);

  // Explore state
  const [amount, setAmount] = useState(ec.amountDefault);
  const [years, setYears] = useState(ec.yearsDefault);
  const [rate, setRate] = useState(ec.rateDefault);

  // Challenge state
  const [roundIdx, setRoundIdx] = useState(0);
  const [challengePhase, setChallengePhase] = useState<'pick' | 'reveal' | 'done'>('pick');
  const [pendingPick, setPendingPick] = useState<number | null>(null);
  const [picks, setPicks] = useState<number[]>([]);

  // Explore computed values
  const finalVal = fv(amount, rate, years);
  const gained = finalVal - amount;
  const mult = amount > 0 ? finalVal / amount : 1;

  // Bar chart — always 10 stable elements so CSS height transition works
  const NUM_BARS = 10;
  const barData = Array.from({ length: NUM_BARS }, (_, i) => {
    const y = Math.max(1, Math.round(((i + 1) / NUM_BARS) * years));
    return { year: y, value: fv(amount, rate, y) };
  });
  const maxBarVal = Math.max(barData[barData.length - 1].value, 1);

  // Challenge helpers
  const round = rounds[Math.min(roundIdx, rounds.length - 1)];
  const stars = picks.filter((p, i) => p === rounds[i]?.bestIdx).length;

  function choiceResult(r: Round, cIdx: number): number {
    const c = r.choices[cIdx];
    return fv(r.startAmount, c.rate, c.years ?? r.years, c.annual ?? 0);
  }

  function confirmPick() {
    if (pendingPick === null) return;
    setPicks(p => [...p, pendingPick]);
    setChallengePhase('reveal');
  }

  function nextRound() {
    if (roundIdx + 1 >= rounds.length) {
      setChallengePhase('done');
    } else {
      setRoundIdx(i => i + 1);
      setPendingPick(null);
      setChallengePhase('pick');
    }
  }

  const lastPick = picks.length > 0 ? picks[picks.length - 1] : null;
  const pickedResult = challengePhase === 'reveal' && lastPick !== null ? choiceResult(round, lastPick) : null;
  const pickedChoice = challengePhase === 'reveal' && lastPick !== null ? round.choices[lastPick] : null;
  const pickedIsCorrect = challengePhase === 'reveal' && lastPick === round.bestIdx;
  const bestResult = choiceResult(round, round.bestIdx);

  // ── MODE PICKER ─────────────────────────────────────────────────────────────
  if (mode === null) {
    return (
      <div style={{ fontFamily: 'Nunito', userSelect: 'none' }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>📈</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: dark, fontFamily: 'Fredoka One', marginBottom: 4 }}>
            Compound Interest
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 700 }}>
            {t === 'starter' ? 'Watch your money grow over time!' :
             t === 'master' ? 'Model your long-term wealth' :
             'See compounding work its magic'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button onClick={() => setMode('explore')} className="btn-bounce"
            style={{
              background: `linear-gradient(135deg, ${color}18, ${color}08)`,
              border: `2.5px solid ${color}66`, borderRadius: 20,
              padding: '18px 16px', cursor: 'pointer', textAlign: 'left',
            }}>
            <div style={{ fontSize: 26, marginBottom: 6 }}>🔬</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: dark, marginBottom: 3 }}>Explorer Mode</div>
            <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 700 }}>
              {t === 'starter' ? 'Slide the controls, watch your piggy bank grow' :
               t === 'master' ? 'Adjust assumptions, see real return projections' :
               'Move sliders and watch the math update live'}
            </div>
          </button>
          <button onClick={() => setMode('challenge')} className="btn-bounce"
            style={{
              background: `linear-gradient(135deg, ${color}, ${dark})`,
              border: 'none', borderRadius: 20,
              padding: '18px 16px', cursor: 'pointer', textAlign: 'left',
            }}>
            <div style={{ fontSize: 26, marginBottom: 6 }}>🎯</div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', marginBottom: 3 }}>Challenge Mode</div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>
              3 rounds — make decisions, see outcomes, earn stars ⭐
            </div>
          </button>
        </div>
      </div>
    );
  }

  // ── EXPLORE MODE ─────────────────────────────────────────────────────────────
  if (mode === 'explore') {
    return (
      <div style={{ fontFamily: 'Nunito', userSelect: 'none' }}>
        <button onClick={() => setMode(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 800, color: '#6b7280', marginBottom: 10, padding: 0 }}>
          ← Back
        </button>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 14 }}>{ec.intro}</div>

        {/* Amount slider */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 800, color: dark }}>
              {t === 'master' ? 'Principal' : t === 'builder' ? 'Starting amount' : 'You save'}
            </label>
            <span style={{ fontSize: 15, fontWeight: 900, color, fontFamily: 'Fredoka One' }}>{fmt(amount)}</span>
          </div>
          <input type="range" min={ec.amountMin} max={ec.amountMax} step={ec.amountStep} value={amount}
            onChange={e => setAmount(Number(e.target.value))}
            style={{ width: '100%', accentColor: color }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', fontWeight: 700, marginTop: 2 }}>
            <span>{fmt(ec.amountMin)}</span><span>{fmt(ec.amountMax)}</span>
          </div>
        </div>

        {/* Years slider */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <label style={{ fontSize: 13, fontWeight: 800, color: dark }}>
              {t === 'master' ? 'Time horizon' : 'Years saved'}
            </label>
            <span style={{ fontSize: 15, fontWeight: 900, color, fontFamily: 'Fredoka One' }}>
              {years} yr{years !== 1 ? 's' : ''}
            </span>
          </div>
          <input type="range" min={ec.yearsMin} max={ec.yearsMax} step={1} value={years}
            onChange={e => setYears(Number(e.target.value))}
            style={{ width: '100%', accentColor: color }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', fontWeight: 700, marginTop: 2 }}>
            <span>{ec.yearsMin}yr</span><span>{ec.yearsMax}yr</span>
          </div>
        </div>

        {/* Rate: presets for starter, slider for builder/master */}
        {ec.ratePresets ? (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: dark, marginBottom: 8 }}>Where do you save it?</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {ec.ratePresets.map(p => (
                <button key={p.rate} onClick={() => setRate(p.rate)} className="btn-bounce"
                  style={{
                    flex: 1, borderRadius: 14, padding: '10px 4px',
                    border: rate === p.rate ? `2.5px solid ${color}` : '2px solid #e5e7eb',
                    background: rate === p.rate ? `${color}18` : '#f9fafb',
                    cursor: 'pointer',
                    boxShadow: rate === p.rate ? `0 2px 8px ${color}33` : 'none',
                    minHeight: 72,
                  }}>
                  <div style={{ fontSize: 22, marginBottom: 3 }}>{p.emoji}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: rate === p.rate ? dark : '#6b7280', lineHeight: 1.2 }}>{p.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: rate === p.rate ? color : '#9ca3af' }}>{p.rate}%/yr</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <label style={{ fontSize: 13, fontWeight: 800, color: dark }}>
                {t === 'master' ? 'Expected CAGR' : 'Annual return'}
              </label>
              <span style={{ fontSize: 15, fontWeight: 900, color, fontFamily: 'Fredoka One' }}>{rate}%</span>
            </div>
            <input type="range" min={ec.rateMin} max={ec.rateMax} step={1} value={rate}
              onChange={e => setRate(Number(e.target.value))}
              style={{ width: '100%', accentColor: color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#9ca3af', fontWeight: 700, marginTop: 2 }}>
              <span>{ec.rateMin}%</span><span>{ec.rateMax}%</span>
            </div>
          </div>
        )}

        {/* Bar chart */}
        <div style={{ background: '#f9fafb', borderRadius: 18, padding: '14px 12px 10px', border: '2px solid #e5e7eb', marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
            📊 Growth Over Time
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 90, paddingBottom: 2 }}>
            {/* Start bar */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: 32, flexShrink: 0 }}>
              <div style={{ fontSize: 8, color: '#9ca3af', fontWeight: 700, height: 12, display: 'flex', alignItems: 'flex-end' }}>{fmt(amount)}</div>
              <div style={{
                width: '100%', background: '#d1d5db', borderRadius: '4px 4px 0 0',
                height: `${Math.max(4, (amount / maxBarVal) * 74)}px`,
                transition: 'height 0.35s ease',
              }} />
              <div style={{ fontSize: 8, color: '#9ca3af', fontWeight: 700 }}>now</div>
            </div>
            {/* Year bars */}
            {barData.map((d, i) => {
              const heightPx = Math.max(4, (d.value / maxBarVal) * 74);
              const isLast = i === NUM_BARS - 1;
              const showLabel = isLast || (i === Math.floor(NUM_BARS / 2) - 1);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ fontSize: 8, fontWeight: 700, height: 12, display: 'flex', alignItems: 'flex-end', color: isLast ? dark : 'transparent' }}>
                    {isLast ? fmt(d.value) : 'x'}
                  </div>
                  <div style={{
                    width: '100%', borderRadius: '4px 4px 0 0',
                    height: `${heightPx}px`,
                    background: isLast
                      ? `linear-gradient(180deg, ${color}, ${dark})`
                      : showLabel
                      ? `${color}88`
                      : `${color}55`,
                    transition: 'height 0.35s ease',
                    boxShadow: isLast ? `0 2px 8px ${color}55` : 'none',
                  }} />
                  <div style={{ fontSize: 8, color: isLast ? dark : '#9ca3af', fontWeight: isLast ? 900 : 700 }}>
                    {isLast ? `yr${d.year}` : showLabel ? `yr${d.year}` : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary panel */}
        <div style={{
          background: `linear-gradient(135deg, ${color}18, ${color}06)`,
          border: `2.5px solid ${color}44`, borderRadius: 18, padding: '14px 16px', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 2 }}>You put in</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#374151', fontFamily: 'Fredoka One' }}>{fmt(amount)}</div>
            </div>
            <div style={{ fontSize: 22 }}>→</div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color, fontWeight: 900, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 1 }}>You end up with</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: dark, fontFamily: 'Fredoka One', transition: 'all 0.3s' }}>{fmt(finalVal)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>Gained</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: gained > 0 ? '#10b981' : '#9ca3af' }}>+{fmt(gained)}</div>
            </div>
            <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>Multiplied</div>
              <div style={{ fontSize: 15, fontWeight: 900, color: dark }}>{mult.toFixed(1)}×</div>
            </div>
            {ec.inflationRate > 0 && (
              <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>Real value</div>
                <div style={{ fontSize: 15, fontWeight: 900, color: '#f59e0b' }}>
                  {fmt(fv(amount, rate - ec.inflationRate, years))}
                </div>
              </div>
            )}
          </div>
          {ec.showFormula && (
            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: '#9ca3af', fontWeight: 700 }}>
              A = {fmt(amount)} × (1 + {rate}%)^{years} = {fmt(finalVal)}
            </div>
          )}
        </div>

        {/* Wow moment callout */}
        {mult >= 2 && (
          <div style={{
            background: `${color}14`, border: `2px solid ${color}44`,
            borderRadius: 14, padding: '10px 14px', marginBottom: 12, textAlign: 'center',
            fontSize: 13, fontWeight: 800, color: dark, animation: 'pop 0.4s',
          }}>
            {mult >= 10
              ? `🤯 Your money grew ${mult.toFixed(0)}× — that's the power of compounding!`
              : mult >= 5
              ? `🔥 ${mult.toFixed(1)}× growth! Compound interest is wild.`
              : `✨ Your money doubled! Keep it in longer to see it 5× or 10×.`}
          </div>
        )}

        <button onClick={() => onComplete()} className="btn-bounce"
          style={{
            width: '100%', background: `linear-gradient(135deg, ${color}, ${dark})`,
            border: 'none', borderRadius: 18, padding: '16px',
            fontSize: 17, fontWeight: 900, color: '#fff', cursor: 'pointer',
            fontFamily: 'Fredoka One', boxShadow: `0 6px 20px ${color}55`,
          }}>
          Done Exploring! ✓
        </button>
      </div>
    );
  }

  // ── CHALLENGE DONE ───────────────────────────────────────────────────────────
  if (challengePhase === 'done') {
    const starLine = stars === 3 ? '🌟🌟🌟' : stars === 2 ? '⭐⭐' : stars === 1 ? '⭐' : '📚';
    return (
      <div style={{ fontFamily: 'Nunito', userSelect: 'none', textAlign: 'center' }}>
        <div style={{ fontSize: 60, marginBottom: 8 }}>{starLine}</div>
        <div style={{ fontFamily: 'Fredoka One', fontSize: 26, color: dark, marginBottom: 4 }}>
          {stars === 3 ? 'Perfect Investor!' : stars >= 2 ? 'Smart Choices!' : stars === 1 ? 'Getting There!' : 'Keep Learning!'}
        </div>
        <div style={{ fontSize: 14, color: '#6b7280', fontWeight: 700, marginBottom: 20 }}>
          You picked the best option {stars} out of {rounds.length} rounds
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20, textAlign: 'left' }}>
          {rounds.map((r, i) => {
            const correct = picks[i] === r.bestIdx;
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: correct ? `${color}12` : '#fee2e222',
                border: `2px solid ${correct ? color + '55' : '#ef444433'}`,
                borderRadius: 14, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{correct ? '✅' : '❌'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', lineHeight: 1.3 }}>
                    Round {i + 1}: {r.choices[picks[i]]?.label ?? '—'}
                  </div>
                  {!correct && (
                    <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginTop: 2 }}>
                      Best: {r.choices[r.bestIdx].label}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={() => onComplete(stars)} className="btn-bounce"
          style={{
            width: '100%', background: `linear-gradient(135deg, ${color}, ${dark})`,
            border: 'none', borderRadius: 18, padding: '16px',
            fontSize: 17, fontWeight: 900, color: '#fff', cursor: 'pointer',
            fontFamily: 'Fredoka One', boxShadow: `0 6px 20px ${color}55`,
            animation: 'pulse 1.2s infinite',
          }}>
          {stars === 3 ? 'Collect Reward! 🪙✨' : 'Finish! →'}
        </button>
      </div>
    );
  }

  // ── CHALLENGE: PICK / REVEAL ─────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'Nunito', userSelect: 'none' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => setMode(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 800, color: '#6b7280', padding: 0 }}>
          ← Back
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          {rounds.map((_, i) => (
            <div key={i} style={{
              width: 28, height: 8, borderRadius: 4,
              background: i < roundIdx ? color : i === roundIdx ? `${color}88` : '#e5e7eb',
              transition: 'background 0.3s',
            }} />
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 900, color, fontFamily: 'Fredoka One' }}>
          {roundIdx + 1}/{rounds.length}
        </div>
      </div>

      {/* Situation card */}
      <div style={{
        background: '#fff', borderRadius: 18, padding: '16px',
        border: `2.5px solid ${color}30`, boxShadow: `0 6px 20px ${color}18`, marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 28 }}>{round.character.split(' ')[0]}</div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700 }}>
            {round.character.split(' ').slice(1).join(' ')}
          </div>
        </div>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#1f2937', lineHeight: 1.5 }}>
          {round.situation}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ background: `${color}18`, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: dark }}>
            💰 {fmt(round.startAmount)}
          </div>
          <div style={{ background: `${color}18`, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 800, color: dark }}>
            ⏳ {round.years} years
          </div>
        </div>
      </div>

      {/* PICK PHASE */}
      {challengePhase === 'pick' && (
        <>
          <div style={{ fontSize: 11, fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8, paddingLeft: 2 }}>
            What would you do?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
            {round.choices.map((c, i) => {
              const picked = pendingPick === i;
              return (
                <button key={i} onClick={() => setPendingPick(i)} className="btn-bounce"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    background: picked ? `${color}18` : '#f9fafb',
                    border: picked ? `2.5px solid ${color}` : '2px solid #e5e7eb',
                    borderRadius: 16, padding: '14px', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.2s',
                    boxShadow: picked ? `0 4px 14px ${color}33` : 'none',
                    minHeight: 56,
                  }}>
                  <div style={{ fontSize: 28, flexShrink: 0 }}>{c.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: picked ? dark : '#374151', lineHeight: 1.3 }}>{c.label}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', fontWeight: 700, marginTop: 2 }}>{c.note}</div>
                  </div>
                  {picked && <div style={{ fontSize: 18, color }}>✓</div>}
                </button>
              );
            })}
          </div>
          <button onClick={confirmPick} disabled={pendingPick === null} className="btn-bounce"
            style={{
              width: '100%',
              background: pendingPick !== null ? `linear-gradient(135deg, ${color}, ${dark})` : '#d1d5db',
              border: 'none', borderRadius: 18, padding: '16px',
              fontSize: 17, fontWeight: 900,
              color: pendingPick !== null ? '#fff' : '#9ca3af',
              cursor: pendingPick !== null ? 'pointer' : 'not-allowed',
              fontFamily: 'Fredoka One',
              boxShadow: pendingPick !== null ? `0 6px 20px ${color}55` : 'none',
              transition: 'all 0.3s',
            }}>
            See What Happens →
          </button>
        </>
      )}

      {/* REVEAL PHASE */}
      {challengePhase === 'reveal' && pickedChoice && pickedResult !== null && (
        <>
          {/* Outcome badge */}
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <div style={{
              display: 'inline-block',
              background: pickedIsCorrect ? `${color}22` : '#fee2e2',
              border: `2.5px solid ${pickedIsCorrect ? color : '#ef4444'}`,
              borderRadius: 14, padding: '6px 18px',
              fontSize: 14, fontWeight: 900,
              color: pickedIsCorrect ? dark : '#991b1b',
              animation: 'pop 0.3s',
            }}>
              {pickedIsCorrect ? '🎯 Best choice!' : '💡 There was a better option'}
            </div>
          </div>

          {/* Result comparison */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{
              flex: 1, background: '#fff', borderRadius: 16, padding: '12px',
              border: `2.5px solid ${pickedIsCorrect ? color : '#e5e7eb'}`,
              boxShadow: pickedIsCorrect ? `0 4px 14px ${color}33` : 'none',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>You chose</div>
              <div style={{ fontSize: 24 }}>{pickedChoice.emoji}</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '4px 0 8px', lineHeight: 1.3 }}>{pickedChoice.label}</div>
              <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, marginBottom: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${bestResult > 0 ? Math.min(100, (pickedResult / bestResult) * 100) : 100}%`,
                  background: pickedIsCorrect ? `linear-gradient(90deg, ${color}, ${dark})` : '#f87171',
                  borderRadius: 6, transition: 'width 0.6s ease',
                }} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: dark, fontFamily: 'Fredoka One' }}>{fmt(pickedResult)}</div>
            </div>

            {!pickedIsCorrect && (
              <div style={{
                flex: 1, background: `${color}0a`, borderRadius: 16, padding: '12px',
                border: `2.5px solid ${color}55`, textAlign: 'center',
              }}>
                <div style={{ fontSize: 10, color, fontWeight: 900, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Best option</div>
                <div style={{ fontSize: 24 }}>{round.choices[round.bestIdx].emoji}</div>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', margin: '4px 0 8px', lineHeight: 1.3 }}>
                  {round.choices[round.bestIdx].label}
                </div>
                <div style={{ background: '#f3f4f6', borderRadius: 6, height: 8, marginBottom: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: '100%', background: `linear-gradient(90deg, ${color}, ${dark})`, borderRadius: 6 }} />
                </div>
                <div style={{ fontSize: 20, fontWeight: 900, color: dark, fontFamily: 'Fredoka One' }}>{fmt(bestResult)}</div>
              </div>
            )}
          </div>

          {/* Lesson callout */}
          <div style={{
            background: `${color}14`, border: `2px solid ${color}44`,
            borderRadius: 14, padding: '12px 14px', marginBottom: 14,
          }}>
            <div style={{ fontSize: 11, fontWeight: 900, color, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>
              💡 The Lesson
            </div>
            <div style={{ fontSize: 14, fontWeight: 800, color: dark, lineHeight: 1.55 }}>{round.lesson}</div>
          </div>

          <button onClick={nextRound} className="btn-bounce"
            style={{
              width: '100%', background: `linear-gradient(135deg, ${color}, ${dark})`,
              border: 'none', borderRadius: 18, padding: '16px',
              fontSize: 17, fontWeight: 900, color: '#fff', cursor: 'pointer',
              fontFamily: 'Fredoka One', boxShadow: `0 6px 20px ${color}55`,
            }}>
            {roundIdx + 1 < rounds.length ? 'Next Round →' : 'See Results! 🌟'}
          </button>
        </>
      )}
    </div>
  );
}
